import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Button, Stack, Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import { useJobTitleExpanderStore } from '../../store';
import * as Styled from './styles';
import * as SX from './sx';
import type { JobTitleItemsProps as Props } from './types';

export const JobTitleItems = memo(({ className, isByCategories }: Props) => {
  const {
    data,
    setSuggestionsItem,
    setSelectedValues,
    selectedValues,
    searchQuery,
  } = useJobTitleExpanderStore();

  const handleClick = (title: string, category?: string) => {
    setSuggestionsItem(title);
    setSelectedValues([...selectedValues, title]);
  };

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!data?.titles || !searchQuery?.trim()) {
      return data?.titles;
    }

    const query = searchQuery.toLowerCase().trim();

    if ('combined' in data.titles) {
      return {
        combined: data.titles.combined.filter((title) =>
          title.toLowerCase().includes(query)
        ),
      };
    }

    const filteredTitles: { [key: string]: string[] } = {};
    Object.entries(data.titles).forEach(([category, titles]) => {
      const filteredCategoryTitles = titles.filter((title) =>
        title.toLowerCase().includes(query)
      );
      if (filteredCategoryTitles.length > 0) {
        filteredTitles[category] = filteredCategoryTitles;
      }
    });

    return filteredTitles;
  }, [data?.titles, searchQuery]);

  return (
    <Styled.Wrapper
      className={classnames('JobTitleItems', className)}
      sx={SX.root}
    >
      {isByCategories ? (
        <Stack gap={1.5}>
          {Object.entries(filteredData || {}).map(([category, titles]) => (
            <Stack key={category} gap={1}>
              <Typography variant="body5" color="grey.500" fontWeight={500}>
                Suggestions based on "{category}"
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1.5}>
                {titles
                  .filter((title) => !selectedValues.includes(title))
                  .map((title, index) => (
                    <Button
                      key={`${category}-${index}`}
                      variant="outlined"
                      size="small"
                      startIcon={<Icon name="plus" />}
                      onClick={() => handleClick(title, category)}
                    >
                      {title}
                    </Button>
                  ))}
              </Stack>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Stack direction="row" flexWrap="wrap" gap={1.5}>
          {Object.values(filteredData || {})
            .flat()
            .filter((title) => !selectedValues.includes(title))
            .map((title, index) => (
              <Button
                key={`flat-${index}`}
                variant="outlined"
                size="small"
                startIcon={<Icon name="plus" />}
                onClick={() => handleClick(title)}
              >
                {title}
              </Button>
            ))}
        </Stack>
      )}
    </Styled.Wrapper>
  );
});

JobTitleItems.displayName = 'JobTitleItems';
